const enUsTranslations = {
  translations: {
    pageTitle: "Housien Authentication",

    help: {
      nextButton: "Next",
      backButton: "Back",
      defaultSelect: "Select an option...",
      showPasswordCheck: "Show password",
      timeForNewCodeText: "Receive new code",
      timeForNewCodeLink: "Send new code",
      check: "Check the checkbox to continue.",
      fieldOrSelect: "The field {{placeholder}} is required.",
      toggleTheme: "Toggle theme",
      toggleThemeToDark: "Switch to dark mode",
      toggleThemeToLight: "Switch to light mode",
      requiredField: "Required field",
      noOptionsFound: "No options found.",
      noCountriesFound: "No countries found.",
      timeForNewCodeCountdown: "({{seconds}} seconds)",
      recaptchaWaiting: "Please wait for security verification to complete...",
      clientLogoAlt: "Client Logo",
      k4dLogoAlt: "K4D Logo",
      housienLogoAlt: "Housien Logo",
      closeAriaLabel: "Close",
      and: "and",
    },

    error: {
      pageTitle: "Error",
      oauth: {
        title: "Oops! Something went wrong",
        message: "We couldn't process your request. Please check if the access link is correct and try again.",
        cta: "Try again",
        missing: "Missing required parameters: {{params}}",
        invalid: "The provided parameters are invalid or incomplete.",
        clientInvalid: "The provided client_id is not valid or not registered in the system.",
        providerError: "Error code: {{code}}\nMessage: {{message}}",
        unexpected: "Unexpected error."
      },
      recaptcha: {
        details: "Our anti-bot service is not responding properly. Please try again later.",
        tokenRequired: "Anti-bot token is required for start_authentication request. Please ensure reCAPTCHA is executed before making this request.",
        tokenNotAvailable: "Anti-bot token is required but not available. Please ensure reCAPTCHA is executed.",
        notReady: "reCAPTCHA is not ready. Please wait for initialization to complete.",
        userFacing: "Security verification failed. Please wait a few seconds and try again.",
        retry: "Try again"
      },
      antiBot: {
        InvalidTokenError: "The security validation failed. Please refresh the page and try again.",
        MissingTokenError: "Security verification was not completed. Please refresh the page and try again."
      },
      social: {
        title: "Social Login Unavailable",
        message: "Your social account doesn't have a verified email address, which is required to sign in.",
        details: "To continue, please add and verify an email address in your social provider (Google, Facebook, etc.), then try logging in again.",
        cta: "Back to login"
      },
      route: {
        title: "Page not found",
        message: "The page you're looking for doesn't exist or has been moved. Please check the address and try again.",
        path: "Requested route: {{path}}",
        cta: "Go back"
      },
      timeout: {
        title: "Timeout expired",
        message: "The server is not responding. Please wait a few moments and try again.",
        details: "The 1 minute timeout was exceeded while waiting for a server response.",
        cta: "Retry"
      },
      general: {
        title: "Something went wrong",
        message: "An unexpected error occurred. Please try again in a few moments.",
        missing: "Required arguments: {{params}}",
        altText: "Error illustration",
        cta: "Go back"
      },
      clientNotFound: {
        title: "Client not found",
        message: "Client ID {{clientId}} is not registered.",
        networkError: "Network error, check your connection."
      },
      help: "We're sorry for the inconvenience. If this keeps happening, please reach out to support.",
      details: {
        show: "View technical details",
        hide: "Hide technical details"
      },
      token: {
        expired: "Authentication token has expired. Please refresh the page.",
        expiringSoon: "Token expires in {{timeString}}. Please complete your action soon."
      },
      recoveryPassword: {
        missingToken: "Missing recovery token. Please use a valid recovery link.",
        invalidLinkTitle: "Invalid Recovery Link"
      },
      runtime: {
        redirectFailed: "Failed to redirect after authentication. Please try again.",
        unexpectedPasswordResponse: "Unexpected response type from password authentication. Please try again.",
        unexpectedSecondFactorResponse: "Unexpected response type from second factor authentication. Please try again.",
        requestTimeout: "Request timed out. Please try again.",
        networkError: "Network error - please check your connection and try again.",
        maxRecoverySends: "Maximum recovery password link sends reached. Contact system administrators.",
        recoveryCooldown: "Please wait {{seconds}} seconds before sending another recovery password link.",
        requestInProgress: "Request already in progress.",
        noIdentityToken: "No valid identity token found. Please complete the identity step first.",
        oauth2Required: "OAuth2 client information is required. Please ensure OAuth2 parameters are provided.",
        missingRecoveryParams: "Missing required recovery parameters. Please use a valid recovery link.",
        invalidAuthToken: "Invalid or expired authentication token.",
        recaptchaUnavailable: "reCAPTCHA is not available.",
        recaptchaFailed: "Failed to get anti-bot token from reCAPTCHA.",
        recaptchaNotReadyTimeout: "reCAPTCHA did not activate within the timeout. Please refresh the page.",
        noIdentityResponse: "No identity response found.",
        noPersonalInfoResponse: "No personal info response found.",
        noPasswordResponse: "No password response found.",
        requestCancelled: "Request was cancelled.",
        antiBotRequired: "Anti-bot token is required for start_authentication request.",
      }
    },

    login: {
      identity: {
        pageTitle: "Sign In",
        title: "Login",
        subtitle: "Use your account to access our services.",
        subtitleTemplate: "Use your {{clientName}} account to access our services.",
        emailPlaceholder: "E-mail",
        emailPlaceholderHint: "your@email.com",
        forgotEmailLink: "Forgot e-mail?",
        guestModeText:
          "Not your computer? Use Guest mode to sign in privately.",
        guestModeLink: "More details please!",
        createAccountButton: "Sign Up",
        nextButton: "Next",
        orConnectWithText: "or",
        continueWithGoogle: "Continue with Google",
        continueWithFacebook: "Continue with Facebook",
        continueWithApple: "Continue with Apple",
        googleUnavailable: "Google authentication is currently unavailable. Please try again later or use another sign-in method.",
        facebookUnavailable: "Facebook authentication is currently unavailable. Please try again later or use another sign-in method.",
        appleUnavailable: "Apple authentication is currently unavailable. Please try again later or use another sign-in method.",
        recaptchaNotice: "Protected by reCAPTCHA. Google Privacy Policy and Terms of Service apply.",
      },

      password: {
        pageTitle: "Password",
        title: "Hi, {{username}}",
        subtitle: "Verify it's you",
        passwordPlaceholder: "Password",
        forgotPasswordLink: "Forgot password?",
        securityWarning: "Never share your password with third parties. Housien never requests your password via phone, email or WhatsApp.",
        securityWarningLink: "Learn more about security.",
      },

      sendRecoveryPasswordLink: {
        pageTitle: "Recover Password",
        title: "Forgot Your Password?",
        subtitle: "Create a new password following the instructions",
        text: "We sent a link to the email {{email}}, access this link to create a new password.",
        initialText: "Click the button below to send a recovery password link to your email {{email}}.",
        timeLabel: "Receive new link",
        linkLabel: "Send new link",
        buttonText: "Send recovery link",
        buttonWaitText: "Wait {{seconds}} seconds",
        buttonSendingText: "Sending...",
        buttonMaxReachedText: "Maximum sends reached",
        cooldownMessage: "You can send another recovery password link in {{seconds}} seconds.",
        sendCountMessage: "Recovery password links sent: {{count}}/3",
        maxSendsMessage: "Unable to send a new recovery password link. Contact system administrators.",
        recoveryLinkSentTitle: "Recovery Link Sent"
      },
      
      
      recoveryPassword: {
        pageTitle: "New Password",
        title: "Create New Password",
        subtitle: "Enter and confirm a new password to access your account"
      },

      secondFactor: {
        pageTitle: "Two-Factor Authentication",
        title: "Two-Factor Verification",
        subtitle: "Enter the verification code sent to your email",
        codePlaceholder: "Insert code"
      }
    },

    signup: {
      createAccount: {
        pageTitle: "Create Account",
        title: "Create a Housien Account",
        subtitle: "Enter your name and nationality",
        namePlaceholder: "First name",
        surnamePlaceholder: "Last name (optional)",
        nationalityPlaceholder: "Where are you from?",
        serviceTermsCheck: "I agree to the",
        serviceTermsLink: "Service Terms",
        privacyPolicyLink: "Privacy Policy",
        loginButton: "Already have an account?",
      },

      birthdayGender: {
        pageTitle: "Personal Info",
        title: "Basic information",
        subtitle: "Enter your birthday and gender",
        dayPlaceholder: "Day",
        monthPlaceholderSelect: "Month",
        monthPlaceholderSelectOptions:
          "January,February,March,April,May,June,July,August,September,October,November,December",
        yearPlaceholder: "Year",
        genderPlaceholderSelect: "Gender",
        genderPlaceholderSelectOptions: "Female,Male,Custom,Rather not say",
        genderPlaceholder: "What's your gender?",
        pronounPlaceholderSelect: "Please refer to me as",
        pronounPlaceholderSelectOptions: "Female,Male,Other",
      },

      insertUsername: {
        pageTitle: "Choose Email",
        title: "How will you log in",
        subtitle: "Enter an email address to log in to your Housien Account",
        emailPlaceholder: "E-mail",
      },

      createPassword: {
        pageTitle: "Create Password",
        title: "Create a Password",
        subtitle:
          "Create a strong password with a mix of letters, numbers and symbols",
        passwordPlaceholder: "Password",
        confirmPasswordPlaceholder: "Confirm Password",
      },

      insertUsernameCode: {
        pageTitle: "Verify Email",
        title: "Verify your E-mail",
        subtitle:
          "Congratulations on creating your Housien Account. Now enter the verification code sent to the email address you provided",
        codePlaceholder: "Insert code",
      },

      congratulations: {
        pageTitle: "Welcome!",
        title: "Housien Account Created",
        subtitle: "Congratulations on finalizing your Housien Account!",
        bodyText:
          "Now log in to your account to enjoy all of our services.",
        loginLink: "Login",
      },

      onboarding: {
        pageTitle: "Complete Registration",
        title: "Welcome, {{name}}",
        subtitle: "One last step to get started.",
        body: "By continuing, you allow K4D to send you important updates about your account, security alerts, and service communications.",
      }
    },

    popUp: {
      titlePrefix: "Oops...",
      titleError: "Something went wrong",
      titleNetworkError: "No connection",
      bodyTextError: "We couldn't complete your request. Please try again.",
      bodyTextNetworkError: "Please check your internet connection and try again.",
      bodyText:
        "If the issue persists, please contact us providing the error code",
      contactSubtitle: "Contact",
      moreDetailsLink: "More Details",
      detailsToggle: "Technical details",
      dismissButton: "Try again",
    },

    countries: {
      AF: "Afghanistan",
      AL: "Albania",
      DZ: "Algeria",
      AD: "Andorra",
      AO: "Angola",
      AG: "Antigua and Barbuda",
      AR: "Argentina",
      AM: "Armenia",
      AU: "Australia",
      AT: "Austria",
      AZ: "Azerbaijan",
      BS: "Bahamas",
      BH: "Bahrain",
      BD: "Bangladesh",
      BB: "Barbados",
      BY: "Belarus",
      BE: "Belgium",
      BZ: "Belize",
      BJ: "Benin",
      BT: "Bhutan",
      BO: "Bolivia",
      BA: "Bosnia and Herzegovina",
      BW: "Botswana",
      BR: "Brazil",
      BN: "Brunei",
      BG: "Bulgaria",
      BF: "Burkina Faso",
      BI: "Burundi",
      CV: "Cape Verde",
      KH: "Cambodia",
      CM: "Cameroon",
      CA: "Canada",
      CF: "Central African Republic",
      TD: "Chad",
      CL: "Chile",
      CN: "China",
      CO: "Colombia",
      KM: "Comoros",
      CG: "Congo",
      CD: "Democratic Republic of the Congo",
      CR: "Costa Rica",
      CI: "Côte d'Ivoire",
      HR: "Croatia",
      CU: "Cuba",
      CY: "Cyprus",
      CZ: "Czech Republic",
      DK: "Denmark",
      DJ: "Djibouti",
      DM: "Dominica",
      DO: "Dominican Republic",
      EC: "Ecuador",
      EG: "Egypt",
      SV: "El Salvador",
      GQ: "Equatorial Guinea",
      ER: "Eritrea",
      EE: "Estonia",
      SZ: "Eswatini",
      ET: "Ethiopia",
      FJ: "Fiji",
      FI: "Finland",
      FR: "France",
      GA: "Gabon",
      GM: "Gambia",
      GE: "Georgia",
      DE: "Germany",
      GH: "Ghana",
      GR: "Greece",
      GD: "Grenada",
      GT: "Guatemala",
      GN: "Guinea",
      GW: "Guinea-Bissau",
      GY: "Guyana",
      HT: "Haiti",
      HN: "Honduras",
      HU: "Hungary",
      IS: "Iceland",
      IN: "India",
      ID: "Indonesia",
      IR: "Iran",
      IQ: "Iraq",
      IE: "Ireland",
      IL: "Israel",
      IT: "Italy",
      JM: "Jamaica",
      JP: "Japan",
      JO: "Jordan",
      KZ: "Kazakhstan",
      KE: "Kenya",
      KI: "Kiribati",
      KP: "North Korea",
      KR: "South Korea",
      KW: "Kuwait",
      KG: "Kyrgyzstan",
      LA: "Laos",
      LV: "Latvia",
      LB: "Lebanon",
      LS: "Lesotho",
      LR: "Liberia",
      LY: "Libya",
      LI: "Liechtenstein",
      LT: "Lithuania",
      LU: "Luxembourg",
      MG: "Madagascar",
      MW: "Malawi",
      MY: "Malaysia",
      MV: "Maldives",
      ML: "Mali",
      MT: "Malta",
      MH: "Marshall Islands",
      MR: "Mauritania",
      MU: "Mauritius",
      MX: "Mexico",
      FM: "Micronesia",
      MD: "Moldova",
      MC: "Monaco",
      MN: "Mongolia",
      ME: "Montenegro",
      MA: "Morocco",
      MZ: "Mozambique",
      MM: "Myanmar",
      NA: "Namibia",
      NR: "Nauru",
      NP: "Nepal",
      NL: "Netherlands",
      NZ: "New Zealand",
      NI: "Nicaragua",
      NE: "Niger",
      NG: "Nigeria",
      MK: "North Macedonia",
      NO: "Norway",
      OM: "Oman",
      PK: "Pakistan",
      PW: "Palau",
      PA: "Panama",
      PG: "Papua New Guinea",
      PY: "Paraguay",
      PE: "Peru",
      PH: "Philippines",
      PL: "Poland",
      PT: "Portugal",
      QA: "Qatar",
      RO: "Romania",
      RU: "Russia",
      RW: "Rwanda",
      KN: "Saint Kitts and Nevis",
      LC: "Saint Lucia",
      VC: "Saint Vincent and the Grenadines",
      WS: "Samoa",
      SM: "San Marino",
      ST: "São Tomé and Príncipe",
      SA: "Saudi Arabia",
      SN: "Senegal",
      RS: "Serbia",
      SC: "Seychelles",
      SL: "Sierra Leone",
      SG: "Singapore",
      SK: "Slovakia",
      SI: "Slovenia",
      SB: "Solomon Islands",
      SO: "Somalia",
      ZA: "South Africa",
      SS: "South Sudan",
      ES: "Spain",
      LK: "Sri Lanka",
      SD: "Sudan",
      SR: "Suriname",
      SE: "Sweden",
      CH: "Switzerland",
      SY: "Syria",
      TW: "Taiwan",
      TJ: "Tajikistan",
      TZ: "Tanzania",
      TH: "Thailand",
      TL: "Timor-Leste",
      TG: "Togo",
      TO: "Tonga",
      TT: "Trinidad and Tobago",
      TN: "Tunisia",
      TR: "Turkey",
      TM: "Turkmenistan",
      TV: "Tuvalu",
      UG: "Uganda",
      UA: "Ukraine",
      AE: "United Arab Emirates",
      GB: "United Kingdom",
      US: "United States",
      UY: "Uruguay",
      UZ: "Uzbekistan",
      VU: "Vanuatu",
      VA: "Vatican City",
      VE: "Venezuela",
      VN: "Vietnam",
      YE: "Yemen",
      ZM: "Zambia",
      ZW: "Zimbabwe"
    },

    warningTexts: {
      oAuth2: {
        usernameFormatError: "Enter a valid email address.",
        usernameInvalidException: "User not registered.",
        tokenExpired: "Session expired. Please login again.",
        passwordFormatError: "Password must contain 8 characters.",
        passwordInvalidException: "Incorrect password. Try again.",
      },

      signup: {
        confirmPassword: "Passwords do not match. Please try again.",
        date: "Enter a valid date.",
        name: "Enter your {{id}} correctly.",
        password: {
          beginning: "Password must contain at least",
          length: "8 characters,",
          number: "one number,",
          uppercase: "one uppercase letter,",
          lowercase: "one lowercase letter,",
          specialCharacter: "one special character,",
        },
        select: "Enter a valid {{id}}.",
        username: "Enter a valid email.",
        usernameAlreadyExistsError: "User already exists.",
        code: "Invalid code. Try again.",
        acceptTerms: "You must accept the service terms.",
      },

      date: {
        year: "Enter a valid year.",
        month: "Enter a valid month.",
        day: "Enter a valid day.",
        age: "Age below the required minimum.",
      },
    },
  },
};

export default enUsTranslations;
